"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.usageRates = exports.usageRate = exports.vehicleBans = exports.dbVehicleBan = exports.noCurrentParkingEvent = exports.freeParkingSpaces = exports.dbParkingEventForUsageRate = exports.dbParkingEvent = exports.parkingEventHistory = exports.parkingEvent = exports.userParkingEventID = exports.parkingEventID = exports.startParkingEvent = exports.recentVehicles = exports.recentVehicle = exports.newVehicleBan = exports.parkingEventDateRange = exports.paramLicensePlate = exports.licensePlate = void 0;
const typebox_1 = require("@sinclair/typebox");
const string_1 = require("../options/string");
const user_1 = require("./user");
/**
 * Defines the format of a vehicle ban.
 */
const vehicleBan = typebox_1.Type.Object({
    reason: typebox_1.Type.String({
        description: 'descriptive reason for the ban',
        example: 'Drove through the entrance gate.',
        maxLength: 65535,
    }),
    startDateTime: typebox_1.Type.String(Object.assign(Object.assign({}, string_1.dateTime), { description: 'start timestamp' })),
    endDateTime: typebox_1.Type.String(Object.assign(Object.assign({}, string_1.dateTime), { description: 'end timestamp' })),
});
/**
 * Defines the format used for storing and processing vehicle license plates.
 */
exports.licensePlate = typebox_1.Type.String(Object.assign(Object.assign({}, string_1.nonBlank), { description: 'licence plate', example: 'ABC-123', minLength: 1, maxLength: 16 }));
/**
 * Unique identifier type used to look up licensePlates when using path parameters.
 */
exports.paramLicensePlate = typebox_1.Type.Object({
    licensePlate: exports.licensePlate,
});
/**
 * Unique identifier type used to look up dates when using path parameters.
 */
exports.parkingEventDateRange = typebox_1.Type.Object({
    startDateTime: typebox_1.Type.String(Object.assign(Object.assign({}, string_1.dateTime), { description: 'start timestamp' })),
    endDateTime: typebox_1.Type.String(Object.assign(Object.assign({}, string_1.dateTime), { description: 'end timestamp' })),
});
/**
 * Defines the format of a new vehicle ban.
 */
exports.newVehicleBan = typebox_1.Type.Intersect([
    vehicleBan,
    typebox_1.Type.Object({
        licensePlate: exports.licensePlate,
    }),
]);
/**
 * Defines the format used for recentVehicles object.
 */
exports.recentVehicle = typebox_1.Type.Object({
    licensePlate: exports.licensePlate,
    bans: typebox_1.Type.Array(vehicleBan, {
        description: 'a list of the vehicle\'s current bans',
    }),
});
/**
 * A list of license plates that is used as the response body of the `/recentVehicles` endpoint.
 * Contains a list of current bans for each vehicle.
 */
exports.recentVehicles = typebox_1.Type.Object({
    recentVehicles: typebox_1.Type.Array(exports.recentVehicle, {
        description: 'a list of recent vehicles with possible ban details',
    }),
});
/**
 * Defines the request body used to start a new parking event.
 */
exports.startParkingEvent = typebox_1.Type.Object({
    licensePlate: exports.licensePlate,
});
/**
 * Unique identifier type used to look up parking events when using path parameters.
 */
exports.parkingEventID = typebox_1.Type.Object({
    parkingEventID: typebox_1.Type.String(Object.assign(Object.assign({}, string_1.uuid), { description: 'unique identifier of a parkingEvent' })),
});
/**
 * Unique identifier type used to look up parking events for a user when using path parameters.
 */
exports.userParkingEventID = typebox_1.Type.Intersect([
    user_1.userID,
    exports.parkingEventID,
]);
/**
 * Defines the format used for parking events when communicating with them with the Frontend.
 */
exports.parkingEvent = typebox_1.Type.Object({
    licensePlate: exports.licensePlate,
    ID: typebox_1.Type.String(Object.assign(Object.assign({}, string_1.uuid), { description: 'unique identifier of the parking event' })),
    startDateTime: typebox_1.Type.String(Object.assign(Object.assign({}, string_1.dateTime), { description: 'start time of the parking event' })),
    endDateTime: typebox_1.Type.Union([
        typebox_1.Type.String(Object.assign(Object.assign({}, string_1.dateTime), { description: 'end time of the parking event' })),
        typebox_1.Type.Null({ description: 'parking event not ended' }),
    ], {
        description: 'end time of the parking event',
        example: null,
    }),
    expiryDateTime: typebox_1.Type.String(Object.assign(Object.assign({}, string_1.dateTime), { description: 'expiry time of the parking event' })),
});
/**
 * A list of parking events that is used when retrieving the parking event history.
 */
exports.parkingEventHistory = typebox_1.Type.Object({
    parkingEvents: typebox_1.Type.Array(exports.parkingEvent, {
        description: 'a list of parking events',
    }),
});
/**
 * The database representation of a parking event, used by the Server component.
 */
exports.dbParkingEvent = typebox_1.Type.Intersect([
    exports.parkingEvent,
    typebox_1.Type.Object({
        userID: typebox_1.Type.String(Object.assign(Object.assign({}, string_1.uuid), { description: 'id of the user who is parking' })),
        licensePlateMatches: typebox_1.Type.Boolean({
            description: 'does the licence plate match with CCTV feed',
            example: false,
        }),
    }),
]);
/**
 * The database representation of a parking event for usage rate calculation, used by the Server component.
 */
exports.dbParkingEventForUsageRate = typebox_1.Type.Object({
    ID: typebox_1.Type.String(Object.assign(Object.assign({}, string_1.uuid), { description: 'unique identifier of the parking event' })),
    startDateTime: typebox_1.Type.String(Object.assign(Object.assign({}, string_1.dateTime), { description: 'start time of the parking event' })),
    endDateTimeForUsageRate: typebox_1.Type.String(Object.assign(Object.assign({}, string_1.dateTime), { description: 'least of the following: '
            + 'end/expiry time of the parking event, start time of the parking event + 18h and current time' })),
    expiryDateTime: typebox_1.Type.String(Object.assign(Object.assign({}, string_1.dateTime), { description: 'expiry time of the parking event' })),
    startDate: typebox_1.Type.String(Object.assign(Object.assign({}, string_1.date), { description: 'start date of the parking event' })),
    endDateForUsageRate: typebox_1.Type.String(Object.assign(Object.assign({}, string_1.date), { description: 'end date of the parking event' })),
});
/**
 * Number of free parking spaces.
 */
exports.freeParkingSpaces = typebox_1.Type.Object({
    freeParkingSpaces: typebox_1.Type.Number({
        description: 'number of free parking spaces',
    }),
});
/**
 * A response body type to indicate that there is no current parking event
 */
exports.noCurrentParkingEvent = typebox_1.Type.Null({
    description: 'no ongoing parking event',
});
/**
 * Used by the Server to store vehicle ban records in the database.
 */
exports.dbVehicleBan = typebox_1.Type.Intersect([
    vehicleBan,
    typebox_1.Type.Object({
        ID: typebox_1.Type.String(Object.assign(Object.assign({}, string_1.uuid), { description: 'identifier of the vehicle ban' })),
        licensePlate: exports.licensePlate,
    }),
]);
/**
 * A list of all vehicle bans.
 */
exports.vehicleBans = typebox_1.Type.Object({
    bans: typebox_1.Type.Array(exports.dbVehicleBan, {
        description: 'a list of all vehicle bans',
    }),
});
/**
 * Defines the format of a usage rate for a single day.
 */
exports.usageRate = typebox_1.Type.Object({
    date: typebox_1.Type.String(Object.assign(Object.assign({}, string_1.date), { description: 'date' })),
    hours: typebox_1.Type.String({
        description: 'total parking hours for this date',
        example: '234:34',
    }),
    usageRate: typebox_1.Type.Number({
        description: 'usage rate for this date',
        example: 0.89,
    }),
});
/**
 * A list of daily usage rates.
 */
exports.usageRates = typebox_1.Type.Object({
    usageRates: typebox_1.Type.Array(exports.usageRate, {
        description: 'a list of daily usage rates',
    }),
});
//# sourceMappingURL=parkingEvent.js.map